<template>
	<Card :title="meetingLetter.display_id" :subtitle="dayjs(meetingLetter.created_at).format(trans('general.dayjs.datetime'))">
		<template #badges>
			<Badge :color="badgeColor" active>{{ trans(`rotations.elections.documentation.meeting_letters.status.${meetingLetter.status}`) }}</Badge>
		</template>
		<LabeledObject v-if="meetingLetter.is_rejected">
			<template #label>{{ trans('general.rejection_reason') }}</template>
			<p>{{ meetingLetter.rejection_reason }}</p>
		</LabeledObject>
		<LabeledObject v-if="meetingLetter.is_pending">
			<template #label>{{ trans('general.rejection_reason') }}</template>
			<textarea v-model="rejectionReason"/>
		</LabeledObject>
		<template #footer>
			<template v-if="meetingLetter.is_pending">
				<PrimaryButton :color="Color.Green" @click.stop="accept(meetingLetter)">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('admin.periods.requests.actions.accept') }}
				</PrimaryButton>
				<PrimaryButton :color="Color.Red" @click.stop="reject(meetingLetter)">
					<i class="fa-solid fa-ban mr-2"/>
					{{ trans('admin.periods.requests.actions.reject') }}
				</PrimaryButton>
			</template>
			<a :href="route('association.representatives.rotations.meeting-letters.show', {rotation: rotation.id, meetingLetter: meetingLetter.id})" target="_blank">
				<SecondaryButton :color="Color.Blue">
					<i class="fa-solid fa-eye mr-2"/>
					{{ trans('general.view') }}
				</SecondaryButton>
			</a>
		</template>
	</Card>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {ref, type Ref, defineProps, type ComputedRef, computed} from 'vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type MeetingLetter} from '@/Types/Periods/Rotations/Documentation/MeetingLetter'
import {getRotationDocumentationStatusColor} from '@/Helpers/Association/Representatives/documentation'

const props = defineProps<{
	rotation:Rotation,
	meetingLetter:MeetingLetter,
}>()

const working:Ref<boolean> = ref(false)
const rejectionReason:Ref<string> = ref('')
const rotation:Ref<Rotation> = ref(props.rotation)
const meetingLetter:Ref<MeetingLetter> = ref(props.meetingLetter)

const badgeColor:ComputedRef<Color> = computed(():Color => {
	return getRotationDocumentationStatusColor(meetingLetter.value)
})

function accept():void{
	if(!working.value && meetingLetter.value.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.meeting-letters.update', {
			rotation: rotation.value.id,
			meetingLetter: meetingLetter.value.id,
		}), {
			action: 'accept',
		}).then(({data}) => {
			meetingLetter.value.status = data.status
			meetingLetter.value.is_pending = data.is_pending
			meetingLetter.value.is_accepted = data.is_accepted
			meetingLetter.value.is_rejected = data.is_rejected
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function reject():void{
	if(!working.value && meetingLetter.value.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.meeting-letters.update', {
			rotation: rotation.value.id,
			meetingLetter: meetingLetter.value.id,
		}), {
			action: 'reject',
			rejection_reason: rejectionReason.value,
		}).then(({data}) => {
			meetingLetter.value.status = data.status
			meetingLetter.value.is_pending = data.is_pending
			meetingLetter.value.is_accepted = data.is_accepted
			meetingLetter.value.is_rejected = data.is_rejected
			meetingLetter.value.rejection_reason = data.rejection_reason
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>